/**
 * API Client for HaloPod
 *
 * Handles all communication with the backend API
 */

import type { TicketContext, AnalysisResult, ChatMessage, ChatImageAttachment, CreateSalesTicketPayload, CreateSalesTicketResponse, CreateDocTicketPayload, CreateDocTicketResponse, AssetContextResponse } from '../types/halopsa';

const API_BASE = import.meta.env.VITE_API_URL || '';
const API_KEY = import.meta.env.VITE_HALOPOD_API_KEY || '';

// Mock data for development without backend
const USE_MOCK = !API_BASE && import.meta.env.DEV;

// =============================================================================
// Session Token Management (in-memory only, NOT localStorage)
// =============================================================================

let sessionToken: string | null = null;
let sessionExpiresAt: number | null = null;

export function setSessionToken(token: string, expiresAt: string): void {
  sessionToken = token;
  sessionExpiresAt = new Date(expiresAt).getTime();
  console.log('[Session] Token set, expires:', expiresAt);
}

export function clearSessionToken(): void {
  sessionToken = null;
  sessionExpiresAt = null;
  console.log('[Session] Token cleared');
}

export function hasValidSession(): boolean {
  if (!sessionToken || !sessionExpiresAt) return false;
  return Date.now() < sessionExpiresAt;
}

/** Export current session token data (for passing to popout windows via postMessage) */
export function getSessionTokenData(): { token: string; expiresAt: string } | null {
  if (!sessionToken || !sessionExpiresAt) return null;
  if (Date.now() >= sessionExpiresAt) return null;
  return { token: sessionToken, expiresAt: new Date(sessionExpiresAt).toISOString() };
}

/** Inject a session token received from a parent window (popout inherits parent's session) */
export function injectSessionToken(token: string, expiresAt: string): void {
  setSessionToken(token, expiresAt);
  console.log('[Session] Token injected from parent window, expires:', expiresAt);
}

// =============================================================================
// Session Creation
// =============================================================================

export interface HaloSessionContext {
  memberId?: string;
  memberName?: string;
  ticketId?: number;
}

export interface SessionResponse {
  token: string;
  expiresAt: string;
  member: {
    id: string;
    name: string;
  };
}

/**
 * Create a session by providing Halo PSA member identity.
 */
export async function createApiSession(haloContext: HaloSessionContext): Promise<SessionResponse> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }

  const url = `${API_BASE}/api/lite/session`;
  console.log('[Session] Creating session:', { url, memberId: haloContext.memberId, hasApiKey: !!API_KEY });

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(haloContext),
  });

  console.log('[Session] Response:', res.status, res.statusText);

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    console.error('[Session] Server error:', data);
    throw new ApiError(data.error || data.message || `Session creation failed: ${res.status}`, res.status);
  }

  const result: SessionResponse = await res.json();
  setSessionToken(result.token, result.expiresAt);
  return result;
}

// =============================================================================
// API Call Infrastructure
// =============================================================================

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  status: number;
  details?: string;

  constructor(message: string, status: number, details?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Get default headers for API requests
 */
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (hasValidSession()) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  } else if (API_KEY) {
    // Fallback for session creation endpoint only
    headers['X-API-Key'] = API_KEY;
  }

  return headers;
}

/**
 * Make a GET request to the API
 */
async function get<T>(endpoint: string, params?: Record<string, string | number>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || 'Request failed',
      response.status,
      errorData.details
    );
  }

  return response.json();
}

/**
 * Make a POST request to the API
 */
async function post<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || 'Request failed',
      response.status,
      errorData.details
    );
  }

  return response.json();
}

/**
 * Make a PUT request to the API
 */
async function put<T>(endpoint: string, data: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || 'Request failed',
      response.status,
      errorData.details
    );
  }

  return response.json();
}

/**
 * Upload with FormData (no Content-Type header - browser sets boundary)
 */
async function upload<T>(endpoint: string, formData: FormData): Promise<T> {
  const headers: HeadersInit = {};
  if (hasValidSession()) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  } else if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || 'Upload failed',
      response.status,
      errorData.details
    );
  }

  return response.json();
}

// Export generic helpers for use by hooks
export const apiGet = get;
export const apiPost = post;
export const apiPut = put;
export const apiUpload = upload;

// ==================== MOCK DATA ====================

const mockTicketContext: TicketContext = {
  ticket: {
    id: 12345,
    summary: 'Email not syncing on Outlook',
    details: 'User reports that Outlook is not syncing emails. Tried restarting Outlook and the computer but issue persists. Last sync was 3 days ago.',
    status: 'Open',
    priority: 'Medium',
    ticketType: 'Incident',
    category: 'Email',
    dateEntered: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    slaResponseTarget: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
    slaFixTarget: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  },
  company: {
    id: 100,
    name: 'Acme Corporation',
  },
  contact: {
    id: 200,
    name: 'John Smith',
    email: 'john.smith@acme.com',
    phone: '555-123-4567',
    jobTitle: 'Director of Operations',
    isVip: true,
  },
  owner: {
    id: 50,
    name: 'Tech Support',
  },
  notes: [
    {
      id: 1,
      text: 'Initial contact made. User confirmed issue started after Windows update.',
      dateCreated: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      createdBy: 'Tech Support',
      isPrivate: false,
    },
    {
      id: 2,
      text: 'Checked Outlook profile settings - no issues found.',
      dateCreated: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      createdBy: 'Tech Support',
      isPrivate: true,
    },
  ],
};

// ==================== API FUNCTIONS ====================

/**
 * Fetch ticket context from Halo PSA
 */
export async function fetchTicketContext(ticketId: number): Promise<TicketContext> {
  if (USE_MOCK) {
    console.log('[API] Using mock data for ticket context');
    return { ...mockTicketContext, ticket: { ...mockTicketContext.ticket, id: ticketId } };
  }

  return get<TicketContext>('/api/lite/context', { ticket_id: ticketId });
}

/**
 * Fetch asset context for a ticket (precision device matching)
 */
export async function fetchAssetContext(
  ticketId: number,
  userId?: number,
  clientId?: number
): Promise<AssetContextResponse> {
  if (USE_MOCK) {
    console.log('[API] Using mock data for asset context');
    return {
      ticket_id: ticketId,
      match_level: 2,
      match_confidence: 'high',
      assets: [
        {
          id: 1001,
          deviceName: 'ACME-WS-042',
          serialNumber: 'SN12345678',
          model: 'Dell OptiPlex 7050',
          manufacturer: 'Dell',
          assetType: 'Workstation',
          userName: 'John Smith',
          warrantyEnd: '2024-03-15',
          purchaseDate: '2019-01-10',
          status: 'Active',
        },
        {
          id: 1002,
          deviceName: 'ACME-LT-018',
          serialNumber: 'SN87654321',
          model: 'Dell Latitude 5540',
          manufacturer: 'Dell',
          assetType: 'Laptop',
          userName: 'John Smith',
          warrantyEnd: '2027-06-01',
          purchaseDate: '2025-02-15',
          status: 'Active',
        },
      ],
    };
  }

  const params: Record<string, string | number> = { ticket_id: ticketId };
  if (userId) params.user_id = userId;
  if (clientId) params.client_id = clientId;

  try {
    return await get<AssetContextResponse>('/api/assets/context', params);
  } catch {
    return { ticket_id: ticketId, match_level: 4, match_confidence: 'low', assets: [] };
  }
}

/**
 * Analyze a ticket with AI
 */
export async function analyzeTicket(ticketId: number): Promise<AnalysisResult> {
  if (USE_MOCK) {
    console.log('[API] Using mock data for analysis');
    // Return mock analysis
    return {
      summary: 'Email synchronization issue affecting a VIP user. Likely related to recent Windows update.',
      sentiment: {
        overall: 'neutral',
        confidence: 75,
        metrics: {
          clarity: { score: 80, label: 'Good', explanation: 'Issue is clearly described' },
          sentiment: { score: 50, label: 'Neutral' },
          empathy: { score: 60, label: 'Moderate' },
          acumen: { score: 70, label: 'Good' },
          cadence: { score: 40, label: 'Slow' },
          disruption: { score: 70, label: 'High' },
          age: { score: 60, label: '2 days' },
          priority: { score: 60, label: 'Medium' },
          chronic: { score: 20, label: 'Not chronic' },
          skills: { required: ['Exchange', 'Outlook'], score: 60 },
          vip: { isVip: true, score: 90 },
          multiUserEvent: { scope: 'Single user', score: 20 },
          responseQuality: { score: 70, label: 'Good' },
        },
        escalation: {
          score: 65,
          recommended: false,
          factors: ['VIP user', 'Business impact'],
        },
      },
      suggestedActions: [
        'Check Microsoft 365 admin center for service status',
        'Verify Outlook profile configuration',
        'Consider recreating Outlook profile',
        'Check for recent Windows updates that might affect email',
      ],
    };
  }

  return post<AnalysisResult>('/api/lite/analyze', { ticket_id: ticketId });
}

/**
 * Send a chat message
 */
export async function sendChatMessage(
  ticketId: number,
  message: string,
  history: ChatMessage[],
  images?: ChatImageAttachment[]
): Promise<{ response: string; sources?: string[] }> {
  if (USE_MOCK) {
    console.log('[API] Using mock data for chat', images?.length ? `(+${images.length} image)` : '');
    return {
      response: `I understand you're asking about this ticket. Based on the context, this appears to be an Outlook email sync issue that started after a Windows update. The user is a VIP (Director of Operations) at Acme Corporation.\n\nHere are some suggested next steps:\n1. Check Microsoft 365 service health\n2. Verify the Outlook profile settings\n3. Consider running the Microsoft Support and Recovery Assistant`,
      sources: ['Microsoft Support', 'Internal KB'],
    };
  }

  // History is sent text-only; image data is only sent for the current message.
  const textHistory = history.map(({ role, content }) => ({ role, content }));
  return post<{ response: string; sources?: string[] }>('/api/lite/chat', {
    ticket_id: ticketId,
    message,
    history: textHistory,
    images,
  });
}

/**
 * Add a note to a ticket
 */
export async function addNote(
  ticketId: number,
  note: string,
  noteType: 'internal' | 'public' | 'resolution' = 'internal'
): Promise<{ success: boolean }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Adding note to ticket', { ticketId, note, noteType });
    return { success: true };
  }

  return post<{ success: boolean }>('/api/lite/note', {
    ticket_id: ticketId,
    note,
    is_private: noteType === 'internal',
    noteType,
  });
}

/**
 * Add time entry to a ticket
 */
export async function addTimeEntry(
  ticketId: number,
  hours: number,
  minutes: number,
  note?: string,
  billable: boolean = true
): Promise<{ success: boolean }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Adding time entry', { ticketId, hours, minutes, note, billable });
    return { success: true };
  }

  return post<{ success: boolean }>('/api/lite/time-entry', {
    ticket_id: ticketId,
    hours,
    minutes,
    note,
    billable,
  });
}

/**
 * Generate a response draft
 */
export async function generateResponse(
  ticketId: number,
  tone: 'professional' | 'friendly' | 'technical',
  length: 'concise' | 'detailed'
): Promise<{ response: string }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Generating response');
    return {
      response: `Dear John,\n\nThank you for reaching out regarding the email synchronization issue you're experiencing with Outlook.\n\nI understand this is impacting your ability to stay connected, and I want to assure you we're treating this with priority.\n\nBased on our investigation, the issue appears to be related to a recent Windows update. Our team is actively working on a resolution and I'll keep you updated on our progress.\n\nIn the meantime, you can access your emails through the Outlook web app at outlook.office.com.\n\nPlease don't hesitate to reach out if you have any questions.\n\nBest regards,\nTech Support`,
    };
  }

  return post<{ response: string }>('/api/lite/generate-response', {
    ticket_id: ticketId,
    tone,
    length,
  });
}

/**
 * Fetch available ticket statuses
 */
export async function fetchStatuses(): Promise<{ statuses: Array<{ id: number; name: string; colour?: string; isOpen?: boolean }> }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Fetching statuses');
    return {
      statuses: [
        { id: 1, name: 'New', isOpen: true },
        { id: 2, name: 'In Progress', isOpen: true },
        { id: 3, name: 'Waiting on Customer', isOpen: true },
        { id: 4, name: 'Resolved', isOpen: false },
        { id: 5, name: 'Closed', isOpen: false },
      ],
    };
  }

  return get<{ statuses: Array<{ id: number; name: string; colour?: string; isOpen?: boolean }> }>('/api/lite/statuses');
}

/**
 * Update ticket status or priority
 */
export async function updateTicket(
  ticketId: number,
  updates: { status_id?: number; priority_id?: number }
): Promise<{ success: boolean }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Updating ticket', { ticketId, updates });
    return { success: true };
  }

  return post<{ success: boolean }>('/api/lite/update-ticket', {
    ticket_id: ticketId,
    ...updates,
  });
}

// ==================== KNOWLEDGE BASE ====================

export interface KBArticle {
  id: number;
  title: string;
  snippet: string;
  category: string | null;
  url: string;
}

export interface KBArticleDetail extends KBArticle {
  content: string;
  contentHtml?: string;
  author: string | null;
  createdDate: string | null;
  modifiedDate: string | null;
  viewCount: number;
}

export interface KBSuggestion extends KBArticle {
  relevanceScore: number;
}

/**
 * Search knowledge base articles
 */
export async function searchKB(
  query: string,
  limit: number = 10
): Promise<{ query: string; count: number; articles: KBArticle[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Searching KB for', query);
    return {
      query,
      count: 2,
      articles: [
        {
          id: 1,
          title: 'How to fix Outlook sync issues',
          snippet: 'This article covers common Outlook synchronization problems and their solutions...',
          category: 'Email',
          url: '#',
        },
        {
          id: 2,
          title: 'Microsoft 365 troubleshooting guide',
          snippet: 'Comprehensive guide for troubleshooting Microsoft 365 applications...',
          category: 'Microsoft',
          url: '#',
        },
      ],
    };
  }

  return get<{ query: string; count: number; articles: KBArticle[] }>('/api/kb/search', { q: query, limit });
}

/**
 * Get a single KB article by ID
 */
export async function getKBArticle(articleId: number): Promise<KBArticleDetail> {
  if (USE_MOCK) {
    console.log('[API] Mock: Getting KB article', articleId);
    return {
      id: articleId,
      title: 'How to fix Outlook sync issues',
      snippet: 'This article covers common Outlook synchronization problems...',
      content: 'Full article content here...\n\n## Steps to resolve\n\n1. Open Outlook settings\n2. Check account configuration\n3. Test connection',
      contentHtml: '<p>Full article content here...</p><h2>Steps to resolve</h2><ol><li>Open Outlook settings</li><li>Check account configuration</li><li>Test connection</li></ol>',
      category: 'Email',
      author: 'Tech Support',
      createdDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      modifiedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      viewCount: 142,
      url: '#',
    };
  }

  return get<KBArticleDetail>('/api/kb/article', { id: articleId });
}

/**
 * Get AI-powered KB suggestions for a ticket
 */
export async function getKBSuggestions(
  ticketId: number
): Promise<{ ticket_id: number; keywords: string[]; count: number; suggestions: KBSuggestion[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Getting KB suggestions for ticket', ticketId);
    return {
      ticket_id: ticketId,
      keywords: ['outlook', 'sync', 'email'],
      count: 2,
      suggestions: [
        {
          id: 1,
          title: 'How to fix Outlook sync issues',
          snippet: 'This article covers common Outlook synchronization problems...',
          category: 'Email',
          url: '#',
          relevanceScore: 9,
        },
        {
          id: 2,
          title: 'Microsoft 365 troubleshooting guide',
          snippet: 'Comprehensive guide for troubleshooting Microsoft 365 applications...',
          category: 'Microsoft',
          url: '#',
          relevanceScore: 7,
        },
      ],
    };
  }

  return post<{ ticket_id: number; keywords: string[]; count: number; suggestions: KBSuggestion[] }>(
    '/api/kb/suggestions',
    { ticket_id: ticketId }
  );
}

// ==================== SIMILAR TICKETS ====================

export interface SimilarTicket {
  id: number;
  summary: string;
  status: string;
  resolution?: string;
  dateEntered: string;
  url: string;
}

/**
 * Find similar tickets
 */
export async function findSimilarTickets(
  ticketId: number,
  keywords?: string[],
  limit: number = 5
): Promise<{ ticket_id: number; count: number; tickets: SimilarTicket[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Finding similar tickets for', ticketId);
    return {
      ticket_id: ticketId,
      count: 2,
      tickets: [
        {
          id: 11111,
          summary: 'Outlook not syncing after update',
          status: 'Resolved',
          resolution: 'Recreated Outlook profile which resolved the sync issue.',
          dateEntered: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
          url: '#',
        },
        {
          id: 22222,
          summary: 'Email sync stopped working',
          status: 'Closed',
          resolution: 'Ran Microsoft Support and Recovery Assistant to fix connection.',
          dateEntered: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          url: '#',
        },
      ],
    };
  }

  return post<{ ticket_id: number; count: number; tickets: SimilarTicket[] }>(
    '/api/records/similar',
    { ticket_id: ticketId, keywords, limit }
  );
}

// ==================== QUICK ACTIONS ====================

export type QuickAction = 'assign' | 'resolve' | 'close' | 'reopen' | 'escalate' | 'update_priority' | 'update_state';

export interface ActionParams {
  ticket_id: number;
  action: QuickAction;
  agent_id?: number;
  resolution?: string;
  priority_id?: number;
  state?: string;
  escalation_reason?: string;
}

/**
 * Perform a quick action on a ticket
 */
export async function performAction(params: ActionParams): Promise<{ success: boolean; ticket?: unknown }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Performing action', params);
    return { success: true };
  }

  return post<{ success: boolean; ticket?: unknown }>('/api/records/actions', params as unknown as Record<string, unknown>);
}

// ==================== SLA ====================

export interface SLAInfo {
  response_target?: string;
  fix_target?: string;
  response_breached?: boolean;
  fix_breached?: boolean;
  response_time_remaining?: number;
  fix_time_remaining?: number;
}

/**
 * Get SLA information for a ticket
 */
export async function getSLAInfo(ticketId: number): Promise<SLAInfo> {
  if (USE_MOCK) {
    console.log('[API] Mock: Getting SLA info for', ticketId);
    return {
      response_target: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
      fix_target: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      response_breached: false,
      fix_breached: false,
      response_time_remaining: 4 * 60 * 60 * 1000,
      fix_time_remaining: 24 * 60 * 60 * 1000,
    };
  }

  return get<SLAInfo>('/api/records/sla', { ticket_id: ticketId });
}

// ==================== ATTACHMENTS ====================

export interface Attachment {
  id: number;
  filename: string;
  filesize?: number;
  contentType?: string;
  uploadedBy?: string;
  uploadedDate?: string;
  url?: string;
}

/**
 * Get attachments for a ticket
 */
export async function getAttachments(ticketId: number): Promise<{ attachments: Attachment[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Getting attachments for', ticketId);
    return {
      attachments: [
        {
          id: 1,
          filename: 'screenshot.png',
          filesize: 245000,
          contentType: 'image/png',
          uploadedBy: 'John Smith',
          uploadedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: 2,
          filename: 'error_log.txt',
          filesize: 12500,
          contentType: 'text/plain',
          uploadedBy: 'Tech Support',
          uploadedDate: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        },
      ],
    };
  }

  return get<{ attachments: Attachment[] }>('/api/records/attachments', { ticket_id: ticketId });
}

// ==================== ENTITLEMENTS/CONTRACTS ====================

export interface Contract {
  id: number;
  name: string;
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  prepayHours?: number;
  prepayHoursRemaining?: number;
}

/**
 * Get contracts/entitlements for a client
 */
export async function getContracts(clientId: number): Promise<{ contracts: Contract[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Getting contracts for client', clientId);
    return {
      contracts: [
        {
          id: 1,
          name: 'Gold Support Plan',
          type: 'Managed Services',
          status: 'Active',
          startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: new Date(Date.now() + 185 * 24 * 60 * 60 * 1000).toISOString(),
          prepayHours: 40,
          prepayHoursRemaining: 12,
        },
      ],
    };
  }

  return get<{ contracts: Contract[] }>('/api/records/entitlements', { client_id: clientId });
}

// ==================== CUSTOMER COMMENTS ====================

/**
 * Add a customer-visible comment to a ticket
 */
export async function addComment(
  ticketId: number,
  comment: string
): Promise<{ success: boolean }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Adding customer comment', { ticketId, comment });
    return { success: true };
  }

  return post<{ success: boolean }>('/api/records/comment', {
    ticket_id: ticketId,
    comment,
  });
}

// ==================== SALES OPPORTUNITY ====================

/**
 * Create a sales opportunity ticket in Halo PSA
 */
export async function createSalesTicket(payload: CreateSalesTicketPayload): Promise<CreateSalesTicketResponse> {
  if (USE_MOCK) {
    console.log('[API] Mock: Creating sales ticket', payload);
    return {
      success: true,
      ticket: {
        id: 99999,
        summary: `Sales Opportunity: ${payload.company_name} - ${payload.opportunity_type}`,
      },
    };
  }

  return post<CreateSalesTicketResponse>('/api/lite/create-sales-ticket', payload as unknown as Record<string, unknown>);
}

// ==================== DOCUMENTATION TICKET ====================

/**
 * Create a documentation request ticket in Halo PSA with optional doc platform document
 */
export async function createDocumentationTicket(payload: CreateDocTicketPayload): Promise<CreateDocTicketResponse> {
  if (USE_MOCK) {
    console.log('[API] Mock: Creating doc ticket', payload);
    return {
      success: true,
      ticket_id: 99998,
      ticket_summary: `Documentation: ${payload.company_name}`,
      doc_url: 'https://example.itglue.com/12345/docs/67890',
      doc_title: 'Mock IT Glue Document',
      doc_provider: 'itglue',
      todo_sections: ['Add network diagram', 'Document VPN configuration'],
    };
  }

  return post<CreateDocTicketResponse>('/api/lite/create-doc-ticket', payload as unknown as Record<string, unknown>);
}

// ==================== LITE PANEL WRAPPERS ====================
// These functions wrap the existing API calls with the types expected by LitePanel

import type { SLAInfo as LiteSLAInfo, Attachment as LiteAttachment, EntitlementResponse } from '@/types/halopsa';

/**
 * Fetch SLA information for a ticket (LitePanel format)
 */
export async function fetchSLA(ticketId: number): Promise<{ slas: LiteSLAInfo[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Fetching SLA for ticket', ticketId);
    return {
      slas: [
        {
          id: '1',
          name: 'Response SLA',
          hasBreached: false,
          percentageUsed: 35,
          timeRemaining: '2h 30m',
        },
        {
          id: '2',
          name: 'Resolution SLA',
          hasBreached: false,
          percentageUsed: 15,
          timeRemaining: '20h 15m',
        },
      ],
    };
  }

  try {
    const result = await get<{ ticket_id: number; slas: LiteSLAInfo[] }>('/api/records/sla', { ticket_id: ticketId });
    return { slas: result.slas || [] };
  } catch {
    return { slas: [] };
  }
}

/**
 * Fetch attachments for a ticket (LitePanel format)
 */
export async function fetchAttachments(ticketId: number): Promise<{ attachments: LiteAttachment[] }> {
  if (USE_MOCK) {
    console.log('[API] Mock: Fetching attachments for ticket', ticketId);
    return {
      attachments: [
        {
          id: 1,
          filename: 'screenshot.png',
          filesize: '245 KB',
          contentType: 'image/png',
          uploadedBy: 'John Smith',
          url: '#',
        },
        {
          id: 2,
          filename: 'error_log.txt',
          filesize: '12 KB',
          contentType: 'text/plain',
          uploadedBy: 'Tech Support',
          url: '#',
        },
      ],
    };
  }

  try {
    const result = await get<{ ticket_id: number; attachments: LiteAttachment[] }>('/api/records/attachments', { ticket_id: ticketId });
    return { attachments: result.attachments || [] };
  } catch {
    return { attachments: [] };
  }
}

/**
 * Fetch entitlements/contracts for a client (LitePanel format)
 */
export async function fetchEntitlements(clientId: number): Promise<EntitlementResponse> {
  if (USE_MOCK) {
    console.log('[API] Mock: Fetching entitlements for client', clientId);
    return {
      client_id: clientId,
      contracts: [
        {
          id: 1,
          name: 'Gold Support Plan',
          type: 'Managed Services',
          status: 'Active',
          prepayHours: 40,
          prepayHoursRemaining: 12,
        },
      ],
    };
  }

  try {
    const result = await get<EntitlementResponse>('/api/records/entitlements', { client_id: clientId });
    return result;
  } catch {
    return { client_id: clientId, contracts: [] };
  }
}
